import { splitOrgPath } from "@/lib/appRoutes";
import { onAuthStateChange } from "@/lib/auth";
import { db, functions, setOrganizationId } from "@/lib/firebase";
import { readLastOrganizationId, writeLastOrganizationId } from "@/lib/organizationPreference";
import {
  type ActiveOrganization,
  pickDefaultOrganization,
  resolveActiveOrganization,
  toOrganizationSummary,
} from "@/lib/organizationSelection";
import { Organization } from "@/types/organization";
import { MembershipSummary, OrganizationSummary, ProvisionUserResponse } from "@/types/provisionUser";
import { TosType, TosVersions } from "@/types/termsOfService";
import { OrganizationRole, UserPermission } from "@/types/user";
import { timeoutPromise } from "@/utils/timeoutPromise";
import { User } from "firebase/auth";
import { collection, doc, DocumentData, getDoc, getDocs, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const SIGN_IN_TIMEOUT_MS = 30000; // 30 seconds

interface AuthContextType {
  user: User | null;
  loading: boolean;
  authError: string | null;
  isAuthenticated: boolean;
  memberships: MembershipSummary[] | undefined; // The user's memberships, with role and ToS acceptance per org
  organizations: OrganizationSummary[] | undefined; // List of organizations the user belongs to
  selectedOrganization: OrganizationSummary | null;
  setSelectedOrganization: (org: OrganizationSummary) => void;
  userRole: OrganizationRole | null; // Current user's role in the selected organization
  isSuperAdmin: boolean; // True if user has superAdmin custom claim
  allOrganizations: OrganizationSummary[] | undefined; // All orgs (for super admins)
  isNewAuth: boolean; // True when user just signed in/up (not page refresh)
  clearNewAuth: () => void; // Call after showing welcome toast
  // Terms of Service
  userPermission: UserPermission | null; // Current user's permission in selected org
  tosVersions: TosVersions | null; // Current ToS versions from /system/termsOfService
  needsTosAcceptance: boolean; // True if user must accept ToS before using app
  tosConfigError: string | null; // Error message if ToS config failed to load
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// The sign-in helpers below only return results; the auth handler commits them,
// and only while its auth event is still the latest one.

// Check if user has super admin custom claim
async function checkSuperAdminStatus(
  identityUser: User
): Promise<{ isSuperAdmin: boolean; allOrganizations: Organization[] | undefined }> {
  try {
    // Force refresh to get latest claims
    const tokenResult = await identityUser.getIdTokenResult(true);
    if (tokenResult.claims.superAdmin === true) {
      console.log("🔑 Super admin access detected");
      return { isSuperAdmin: true, allOrganizations: await fetchAllOrganizations() };
    }
    return { isSuperAdmin: false, allOrganizations: undefined };
  } catch (error) {
    console.error("Error checking super admin status:", error);
    return { isSuperAdmin: false, allOrganizations: undefined };
  }
}

// Fetch all organizations (for super admins), sorted by name
async function fetchAllOrganizations(): Promise<Organization[]> {
  try {
    const orgsRef = collection(db, "organizations");
    const orgsSnapshot = await getDocs(orgsRef);

    const orgs: Organization[] = orgsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...(doc.data() as Organization),
    }));

    // Sort by name for easier selection
    orgs.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    console.log(`📋 Loaded ${orgs.length} organizations for super admin`);
    return orgs;
  } catch (error) {
    console.error("Error fetching all organizations:", error);
    return [];
  }
}

// Fetch ToS versions from /system/termsOfService
async function fetchTosVersions(): Promise<{ tosVersions: TosVersions | null; tosConfigError: string | null }> {
  try {
    const tosDocRef = doc(db, "system/termsOfService");
    const tosDoc = await getDoc(tosDocRef);

    if (tosDoc.exists()) {
      const data = tosDoc.data() as TosVersions;
      console.log("📋 Loaded ToS versions:", data);
      return { tosVersions: data, tosConfigError: null };
    }
    console.error("📋 ToS configuration not found");
    return {
      tosVersions: null,
      tosConfigError: "Terms of Service configuration not found. Please contact support.",
    };
  } catch (error) {
    console.error("Error fetching ToS versions:", error);
    return { tosVersions: null, tosConfigError: "Failed to load Terms of Service configuration. Please try again." };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>();
  const [memberships, setMembershipsState] = useState<MembershipSummary[] | undefined>(undefined);
  // The selected org, the role and the permission change together, in one state update.
  const [activeOrg, setActiveOrg] = useState<ActiveOrganization | null>(null);
  const [isSuperAdmin, setIsSuperAdminState] = useState(false);
  const [allOrganizations, setAllOrganizations] = useState<Organization[] | undefined>(undefined);
  const [isNewAuth, setIsNewAuth] = useState(false);
  const hasReceivedInitialAuth = useRef(false);
  // Bumped by every auth event (sign-in, sign-out, same-user re-fire). Sign-in work commits
  // only while its event is still the latest, so a superseded request (a previous account,
  // or a session signed out meanwhile) can never restore its results.
  const authGenRef = useRef(0);
  // Terms of Service state
  const [tosVersions, setTosVersions] = useState<TosVersions | null>(null);
  const [tosConfigError, setTosConfigError] = useState<string | null>(null);

  // Mirrors read by the stable setSelectedOrganization, written wherever the state is set.
  const userRef = useRef<User | null>(null);
  const membershipsRef = useRef<MembershipSummary[] | undefined>(undefined);
  const isSuperAdminRef = useRef(false);

  const setUser = (next: User | null) => {
    userRef.current = next;
    setUserState(next);
  };
  const setMemberships = (next: MembershipSummary[] | undefined) => {
    membershipsRef.current = next;
    setMembershipsState(next);
  };
  const setIsSuperAdmin = (next: boolean) => {
    isSuperAdminRef.current = next;
    setIsSuperAdminState(next);
  };

  const organizations = useMemo(() => memberships?.map(toOrganizationSummary), [memberships]);

  const clearNewAuth = () => setIsNewAuth(false);

  const activateOrganization = useCallback(
    (
      org: OrganizationSummary,
      context: { memberships: MembershipSummary[] | undefined; isSuperAdmin: boolean; uid: string | undefined }
    ) => {
      setActiveOrg(resolveActiveOrganization(org, context.memberships, context.isSuperAdmin));
      setOrganizationId(org.id ?? ""); // Update global variable for non-React modules
      if (context.uid && org.id) {
        writeLastOrganizationId(context.uid, org.id);
      }
    },
    []
  );

  // Stable identity: callers invoke it from effects and handlers.
  const setSelectedOrganization = useCallback(
    (org: OrganizationSummary) => {
      // Nobody signed in (e.g. a switch that resolved after sign-out), or an account still
      // provisioning (memberships not loaded yet): a late call from a previous session must
      // not activate its org, role or last-used preference for the account signing in.
      if (!userRef.current || membershipsRef.current === undefined) return;
      activateOrganization(org, {
        memberships: membershipsRef.current,
        isSuperAdmin: isSuperAdminRef.current,
        uid: userRef.current?.uid,
      });
    },
    [activateOrganization]
  );

  useEffect(() => {
    // Clears everything one session knew (memberships, the active org and its global id,
    // claims, the org inventory, terms and errors), leaving `loading` as given.
    const resetSession = ({ loading }: { loading: boolean }) => {
      setLoading(loading);
      setMemberships(undefined);
      setActiveOrg(null);
      setOrganizationId("");
      setIsSuperAdmin(false);
      setAllOrganizations(undefined);
      setTosVersions(null);
      setTosConfigError(null);
      setAuthError(null);
    };

    const unsubscribe = onAuthStateChange(async (user) => {
      const gen = ++authGenRef.current;
      const isCurrent = () => gen === authGenRef.current;
      const previousUid = userRef.current?.uid; // the previous event's user, read before the mirror updates
      setUser(user);

      if (!user) {
        hasReceivedInitialAuth.current = true;
        resetSession({ loading: false });
        return;
      }

      if (user.uid !== previousUid) {
        // A different account, or the first since sign-out: show nothing of the
        // previous one (or a stale org id) while this one provisions.
        resetSession({ loading: true });
      }

      // Detect fresh sign-in vs page refresh:
      if (hasReceivedInitialAuth.current) {
        setIsNewAuth(true);
      }

      hasReceivedInitialAuth.current = true;

      try {
        const provisionUserCall = httpsCallable<object, ProvisionUserResponse>(functions, "provisionUser");

        const promises = Promise.all([provisionUserCall({}), fetchTosVersions(), checkSuperAdminStatus(user)]);
        const [
          {
            data: { memberships },
          },
          tos,
          { isSuperAdmin: superAdmin, allOrganizations: allOrgs },
        ] = (await Promise.race([promises, timeoutPromise(SIGN_IN_TIMEOUT_MS)])) as Awaited<typeof promises>;
        if (!isCurrent()) return;

        setTosVersions(tos.tosVersions);
        setTosConfigError(tos.tosConfigError);
        setIsSuperAdmin(superAdmin);
        setAllOrganizations(allOrgs);
        setMemberships(memberships);

        // A reachable org named by the URL (a deep link) beats the last-used org.
        const defaultOrg = pickDefaultOrganization({
          memberships,
          allOrganizations: allOrgs,
          isSuperAdmin: superAdmin,
          lastUsedOrgId: readLastOrganizationId(user.uid),
          preferredOrgId: splitOrgPath(window.location.pathname)?.orgId ?? null,
        });
        if (defaultOrg) {
          activateOrganization(defaultOrg, { memberships, isSuperAdmin: superAdmin, uid: user.uid });
        } else {
          // Never leave a previous account's organization (and its role) active.
          setActiveOrg(null);
          setOrganizationId("");
        }

        setAuthError(null);
      } catch {
        if (isCurrent()) setAuthError("Something went wrong signing in. Please try again.");
      } finally {
        if (isCurrent()) setLoading(false);
      }
    });

    return unsubscribe;
  }, [activateOrganization]); // stable: subscribes once

  const selectedOrganization = activeOrg?.organization ?? null;
  const userRole: OrganizationRole | null = activeOrg?.role ?? null;
  const userPermission: UserPermission | null = activeOrg?.permission ?? null;

  // Refine the active org's role from its own permission doc, so a role change made
  // mid-session, or an org joined after sign-in, reaches the UI. Activation still
  // resolves from the membership snapshot, so no render pairs one org with another
  // org's role; this only corrects the same org afterwards. Keyed on the selected
  // organization object, which changes on every activation (never on a refinement),
  // so selecting the active org again re-reads its permission.
  useEffect(() => {
    const uid = user?.uid;
    const orgId = selectedOrganization?.id;
    if (!uid || !orgId) return;

    let subscribed = true;
    const apply = (data: DocumentData | undefined) => {
      if (!subscribed || userRef.current?.uid !== uid) return;
      // Rules let only active members read their own permission doc, so a missing,
      // disabled or unreadable doc means no membership role: fail closed.
      const permission = data?.status === "active" ? ({ ...data } as UserPermission) : null;
      const role: OrganizationRole | null = permission?.role ?? (isSuperAdmin ? "admin" : null);
      const tosAcceptedVersion = permission?.tosAcceptedVersion ?? null;

      setActiveOrg((prev) => {
        if (prev?.organization.id !== orgId) return prev;
        const unchanged =
          prev.role === role &&
          !prev.permission === !permission &&
          (prev.permission?.tosAcceptedVersion ?? null) === tosAcceptedVersion;
        return unchanged ? prev : { ...prev, role, permission };
      });

      // Keep the membership list fresh so a later switch back, and the picker badge,
      // start from this role. A removed or disabled membership stays listed: dropping
      // it could send the app to RequestAccess mid-session.
      const current = membershipsRef.current;
      if (!permission || !current) return;
      const existing = current.find((m) => m.orgId === orgId);
      if (existing) {
        if (existing.role !== permission.role || existing.tosAcceptedVersion !== tosAcceptedVersion) {
          setMemberships(
            current.map((m) => (m.orgId === orgId ? { ...m, role: permission.role, tosAcceptedVersion } : m))
          );
        }
      } else {
        setMemberships([
          ...current,
          {
            orgId,
            orgName: selectedOrganization.name,
            orgEmail: selectedOrganization.email ?? "",
            orgTosType: selectedOrganization.tosType ?? "standardDataSharing",
            role: permission.role,
            tosAcceptedVersion,
          },
        ]);
      }
    };

    const unsubscribe = onSnapshot(
      doc(db, `organizations/${orgId}/userPermissions/${uid}`),
      (snapshot) => apply(snapshot.exists() ? snapshot.data() : undefined),
      () => apply(undefined)
    );
    return () => {
      subscribed = false;
      unsubscribe();
    };
  }, [user?.uid, selectedOrganization, isSuperAdmin]);

  // Set loading to false only when we have a selected organization
  // This ensures hooks don't run until ORGANIZATION_ID is set
  useEffect(() => {
    if (selectedOrganization && loading) {
      setLoading(false);
    }
  }, [selectedOrganization, loading]);

  // Compute whether user needs to accept Terms of Service
  const needsTosAcceptance = useMemo(() => {
    // Super admins bypass ToS
    if (isSuperAdmin) return false;

    // Wait for data to load
    if (!activeOrg || !tosVersions) return false;

    // Get org's ToS type (default to standardDataSharing)
    const orgTosType = (activeOrg.organization.tosType as TosType) || "standardDataSharing";

    // Custom ToS orgs bypass in-app ToS (they have offline agreements)
    if (orgTosType === "customNoDataSharing") return false;

    // No permission (e.g. a super admin's non-member org) - don't show ToS gate
    const permission = activeOrg.permission;
    if (!permission) return false;

    // Check if user has accepted the current version
    const currentVersion =
      tosVersions[orgTosType as keyof Pick<TosVersions, "standardDataSharing" | "standardNoDataSharing">];
    if (!currentVersion) return false; // No version configured for this type

    const expectedVersion = `${orgTosType}-v${currentVersion}`;
    return permission.tosAcceptedVersion !== expectedVersion;
  }, [isSuperAdmin, activeOrg, tosVersions]);

  const value: AuthContextType = {
    user,
    loading,
    authError,
    isAuthenticated: !!user,
    memberships,
    organizations,
    selectedOrganization,
    setSelectedOrganization,
    userRole,
    isSuperAdmin,
    allOrganizations,
    isNewAuth,
    clearNewAuth,
    // Terms of Service
    userPermission,
    tosVersions,
    needsTosAcceptance,
    tosConfigError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
